/**
 * King Living Checkbox Theme
 *
 * Global checkbox styling system supporting:
 * - 4 states: default, checked, default disabled, checked disabled
 * - Square with slightly rounded corners
 * - Light grey border for active states
 * - Darker grey border for disabled states
 * - Black background when checked
 * - Medium grey background when checked and disabled
 * - Custom checkmark icon
 *
 * @override Extends @salesforce/retail-react-app checkbox theme
 */

import baseCheckbox from '@salesforce/retail-react-app/app/theme/components/base/checkbox';

export default {
  ...baseCheckbox,

  baseStyle: {
    ...baseCheckbox.baseStyle,
    control: {
      ...baseCheckbox.baseStyle.control,
      // Default state: white background, light beige-grey border
      w: 8, // 32px
      h: 8, // 32px
      borderWidth: '1.5px', // 2px border as per design
      borderStyle: 'solid',
      borderColor: 'brandColors.linen03', // Light beige-grey border
      backgroundColor: 'white',
      borderRadius: '6px', // Significantly rounded corners
      transition: 'all 0.2s ease',
      padding: '4px', // Spacing between border and content
      display: 'flex', // Flexbox for centering
      alignItems: 'center', // Center vertically
      justifyContent: 'center', // Center horizontally
      position: 'relative', // For positioning

      // Checked state: transparent container, child span with black background, centered icon
      _checked: {
        'backgroundColor': 'transparent', // Transparent container background
        'borderColor': 'brandColors.linen03', // Light beige-grey border (same as default)
        'color': 'white', // White checkmark icon
        '_hover': {
          borderColor: 'brandColors.linen03',
        },
        // Inline CSS to target direct child div element (icon wrapper)
        '& > div': {
          width: '100%',
          height: '100%',
          backgroundColor: 'brandColors.greyscale', // Black background
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '4px',
        },
        // Checked disabled state: transparent container, child span with medium grey background
        '_disabled': {
          'backgroundColor': 'transparent', // Transparent container background
          'borderColor': 'brandColors.greyscale50', // Medium grey border
          'color': 'white', // White checkmark icon
          'cursor': 'not-allowed',
          'opacity': 1,
          // Inline CSS to target direct child div element in disabled checked state
          '& > div': {
            width: '100%',
            height: '100%',
            backgroundColor: 'brandColors.greyscale50', // Medium grey background
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
          },
        },
      },

      // Default disabled state: white background, medium grey border
      _disabled: {
        backgroundColor: 'white',
        borderColor: 'brandColors.greyscale50', // Medium grey border for disabled
        cursor: 'not-allowed',
        opacity: 1,
      },
    },
    // Icon wrapper div element - child div with black background
    icon: {
      ...(baseCheckbox.baseStyle?.icon || {}),
      // Default: no background for unchecked state
      backgroundColor: 'transparent',
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px', // Slightly less rounded than container
      // Checked state: div wrapper with black background and 100% width
      _checked: {
        width: '100%', // 100% width for the wrapper div
        height: '100%',
        backgroundColor: 'brandColors.greyscale', // Black background
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Checked disabled state: medium grey background
        _disabled: {
          width: '100%', // 100% width for the wrapper div
          backgroundColor: 'brandColors.greyscale50', // Medium grey background
        },
      },
    },
    label: {
      ...baseCheckbox.baseStyle.label,
    },
    container: {
      ...baseCheckbox.baseStyle.container,
    },
  },

  sizes: {
    ...baseCheckbox.sizes,
    md: {
      ...baseCheckbox.sizes.md,
      control: {
        w: 8, // 32px
        h: 8, // 32px
      },
    },
  },
};
